import ReactDom from 'react-dom';

import { Icon as BlueIcon } from '@blueprintjs/core';

import { useContext, useMemo, useState } from 'react';
import { NavigationContext } from '@layout/navigation/navigation';

import { useMutation, useQuery, useQueryClient } from 'react-query';
import { patchAssetById } from '@services/queries/onboarding/patchAssetById';

import { getManufacturersNames } from '@services/queries/manufacturers';
import { IOnboardingGroup } from '@models/OnboardingGroup.model';
import { useParams } from 'react-router-dom';
import {
  ActionButtons,
  Cancel,
  Checkbox,
  CheckContainer,
  CheckText,
  Close,
  Confirm,
  Container,
  DividerKeep,
  Header,
  HorizontalDivider,
  Input,
  InputContainer,
  Label,
  Line,
  LineContainer,
  Overlay,
  SearchedManufacturersCheckBoxes,
  SuggestionsCheckBoxWrapper,
  TextDescription,
  TextsRowFlex,
  TextsRowFlexItem,
  TextTitle,
  Title,
  TitleText,
  ToolTipText,
} from './ManufacturerErrorDialog.styles';
import { FieldErrorTypes } from '../OnboardingFile/OnboardingFile.query';
import Labels from '../Labels/Labels';
import { ConfirmDialog } from '../ConfirmDialog/ConfirmDialog';
import { AddUnmatchedManufacturer } from '../AddUnmatchedManufacturer/AddUnmatchedManufacturer';

interface IManufacturerErrorDialog {
  open: boolean;
  onClose: () => void;
  group: IOnboardingGroup;
  field: FieldErrorTypes;
}

export const ManufacturerErrorDialog = ({
  open,
  onClose,
  group,
  field,
}: IManufacturerErrorDialog) => {
  const portalDiv = document.getElementById('portal');

  const queryClient = useQueryClient();

  const { studioId } = useContext(NavigationContext);

  const params = useParams<{ id: string }>();

  const [search, setSearch] = useState<string>('');
  const [selected, setSelected] = useState<string | null>(null);
  const [openConfirm, setOpenConfirm] = useState<boolean>(false);
  const [continueWithCurrent, setContinueWithCurrent] =
    useState<boolean>(false);
  const [continueWithSearch, setContinueWithSearch] = useState<boolean>(false);
  const [listFrom, setListFrom] = useState<'suggestion' | 'search'>(
    'suggestion',
  );
  const [openUnmatched, setOpenUnmatched] = useState<boolean>(false);

  const suggestions = useMemo(() => {
    if (field === 'manufacturer') {
      return group.mfrSuggestions;
    }

    return [];
  }, [group]);

  const errorMessage = useMemo(() => {
    if (field === 'manufacturer') {
      return group.mfrError;
    }

    if (field === 'model') {
      return group.mpnError;
    }

    return group.productNameError;
  }, [group]);

  const disabled = useMemo(() => {
    if (selected) {
      return false;
    }

    return true;
  }, [selected]);

  const manufacturersQuery = useQuery(
    ['manufacturers', 'names', search],
    () => {
      return getManufacturersNames(search, 5);
    },
  );

  const patchAssetMutation = useMutation(patchAssetById, {
    onSuccess: () => {
      queryClient.invalidateQueries(['onboarding', 'files']);
      onClose();
    },
  });

  return open && portalDiv
    ? ReactDom.createPortal(
        <Overlay>
          <Container>
            <Header>
              <Title>
                <BlueIcon icon="error" color="#D14343" />
                <TitleText> Manufacturer Error</TitleText>
              </Title>
              <Close onClick={onClose}>
                <BlueIcon icon="cross" color="#8F95B2" />
              </Close>
            </Header>

            <TextsRowFlex>
              {group.manufacturer && (
                <TextsRowFlexItem>
                  <TextTitle>Original Input</TextTitle>
                  <TextDescription>{group.manufacturer}</TextDescription>
                </TextsRowFlexItem>
              )}

              <TextsRowFlexItem>
                {errorMessage && (
                  <Labels
                    type="error"
                    text={errorMessage}
                    toolTipId="manufacturerTooltipId"
                    toolTipContent={
                      <>
                        <ToolTipText>
                          Manufacturer is either incorrect or does not exist.
                        </ToolTipText>
                      </>
                    }
                  />
                )}
              </TextsRowFlexItem>
            </TextsRowFlex>

            <DividerKeep />

            <SuggestionsCheckBoxWrapper>
              <CheckContainer
                onClick={() => {
                  setSelected(group.manufacturer);
                  setContinueWithCurrent(true);
                }}
              >
                <Checkbox
                  active={
                    selected === group.manufacturer && continueWithCurrent
                  }
                />
                <CheckText>{`Continue with "${group.manufacturer}"`}</CheckText>
              </CheckContainer>
            </SuggestionsCheckBoxWrapper>

            <HorizontalDivider />

            {!!suggestions.length && (
              <>
                <Label>
                  Manufacturer Name suggestions{' '}
                  <span>(based on Original Input)</span>
                </Label>

                <SuggestionsCheckBoxWrapper>
                  {suggestions.map(suggestion => (
                    <CheckContainer
                      key={suggestion}
                      onClick={() => {
                        setSelected(suggestion);
                        setContinueWithCurrent(false);
                        setContinueWithSearch(false);
                        setListFrom('suggestion');
                      }}
                    >
                      <Checkbox
                        active={
                          selected === suggestion &&
                          listFrom === 'suggestion' &&
                          !continueWithSearch &&
                          !continueWithCurrent
                        }
                      />
                      <CheckText>{suggestion}</CheckText>
                    </CheckContainer>
                  ))}
                </SuggestionsCheckBoxWrapper>

                <HorizontalDivider />
              </>
            )}

            <Label>Search for a Manufacturer</Label>

            <InputContainer>
              <BlueIcon icon="search" color="#8F95B2" />
              <Input
                placeholder="Placeholder"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              {!!search.length && (
                <BlueIcon
                  icon="small-cross"
                  color="#8F95B2"
                  onClick={() => {
                    setSearch('');
                    setSelected(null);
                  }}
                />
              )}
            </InputContainer>

            <SearchedManufacturersCheckBoxes>
              {manufacturersQuery.data?.map(manufacturer => (
                <CheckContainer
                  key={manufacturer.id}
                  onClick={() => {
                    setSelected(manufacturer.name);
                    setContinueWithSearch(false);
                    setContinueWithCurrent(false);
                    setListFrom('search');
                  }}
                >
                  <Checkbox
                    active={
                      selected === manufacturer.name &&
                      listFrom === 'search' &&
                      !continueWithSearch &&
                      !continueWithCurrent
                    }
                  />
                  <CheckText>{manufacturer.name}</CheckText>
                </CheckContainer>
              ))}
              {search && (
                <CheckContainer
                  onClick={() => {
                    setSelected(search);
                    setContinueWithSearch(true);
                  }}
                >
                  <Checkbox
                    active={selected === search && continueWithSearch}
                  />
                  <CheckText>{`Continue with "${search}"`}</CheckText>
                </CheckContainer>
              )}
            </SearchedManufacturersCheckBoxes>

            <LineContainer>
              <Line>
                <ActionButtons>
                  <Cancel type="button" onClick={onClose}>
                    Cancel
                  </Cancel>
                  <Confirm
                    disabled={disabled}
                    onClick={() => {
                      if (continueWithCurrent || continueWithSearch) {
                        setOpenUnmatched(true);
                      } else {
                        setOpenConfirm(true);
                      }
                    }}
                  >
                    Confirm
                  </Confirm>
                </ActionButtons>
              </Line>
            </LineContainer>

            {openUnmatched && selected && (
              <AddUnmatchedManufacturer
                open={openUnmatched}
                onClose={() => setOpenUnmatched(false)}
                group={group}
                closeFlow={onClose}
                newManufacturer={selected}
              />
            )}
            {openConfirm && (
              <ConfirmDialog
                open={openConfirm}
                onClose={() => setOpenConfirm(false)}
                isLoading={patchAssetMutation.isLoading}
                onConfirm={() => {
                  patchAssetMutation.mutate({
                    groupId: group.id,
                    studioId,
                    mfr: selected,
                    mfrError: null,
                    fileId: params.id,
                  });
                }}
                showAsset
                isLarge
                group={group}
                title="Internal Product added"
                description="This type of product is added immediatelly, but no additional product data can be included by Beam. However, you’ll be able to add your own information."
              />
            )}
          </Container>
        </Overlay>,
        portalDiv,
      )
    : null;
};
